/**
 * GridLayoutWrapper Component
 * Encapsulates react-grid-layout with responsive behavior and theme support
 */

import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { Responsive } from 'react-grid-layout';
import {
    GRID_CONFIG,
    DRAG_CONFIG,
    RESIZE_CONFIG,
    BREAKPOINTS,
    COLS
} from '../utils/layoutConstants';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

/**
 * GridLayoutWrapper - Provides responsive grid layout with drag-and-drop and resize capabilities
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.layouts - Layout configuration for all breakpoints and tabs
 * @param {Function} props.onLayoutChange - Callback when layout changes
 * @param {React.ReactNode} props.children - Grid items to render
 * @param {string} props.mode - Theme mode ('light' or 'dark')
 * @param {string} props.tabId - Identifier for the current tab
 * @returns {React.ReactElement} Rendered responsive grid layout
 */
function GridLayoutWrapper({
    layouts,
    onLayoutChange,
    children,
    mode,
    tabId
}) {
    const containerRef = useRef(null);
    const [width, setWidth] = useState(1280);

    // Measure container width on mount and window resize
    useEffect(() => {
        const measureWidth = () => {
            if (containerRef.current) {
                setWidth(containerRef.current.offsetWidth);
            }
        };

        measureWidth();
        window.addEventListener('resize', measureWidth);
        return () => window.removeEventListener('resize', measureWidth);
    }, []);

    // Extract layouts for the current tab from the nested structure
    const tabLayouts = useMemo(() => {
        if (!layouts || !tabId) return {};

        const extracted = {};
        Object.keys(layouts).forEach(breakpoint => {
            if (layouts[breakpoint] && layouts[breakpoint][tabId]) {
                extracted[breakpoint] = layouts[breakpoint][tabId];
            }
        });
        return extracted;
    }, [layouts, tabId]);

    const handleLayoutChange = useCallback(
        (layout, allLayouts) => {
            if (onLayoutChange) {
                // Reconstruct the full nested layout structure for persistence
                const fullLayouts = { ...layouts };
                Object.keys(allLayouts).forEach(breakpoint => {
                    if (!fullLayouts[breakpoint]) {
                        fullLayouts[breakpoint] = {};
                    }
                    fullLayouts[breakpoint][tabId] = allLayouts[breakpoint];
                });
                onLayoutChange(layout, fullLayouts, tabId);
            }
        },
        [onLayoutChange, tabId, layouts]
    );

    const wrapperStyle = {
        width: '100%',
        backgroundColor: mode === 'light' ? '#fff' : '#1e1e1e',
        transition: 'background-color 0.3s'
    };

    return (
        <div ref={containerRef} style={wrapperStyle}>
            <Responsive
                layouts={tabLayouts}
                breakpoints={BREAKPOINTS}
                cols={COLS}
                rowHeight={GRID_CONFIG.rowHeight}
                margin={GRID_CONFIG.margin}
                containerPadding={GRID_CONFIG.containerPadding}
                compactType={GRID_CONFIG.compactType}
                preventCollision={false}
                useCSSTransforms={true}
                isDraggable={DRAG_CONFIG.enabled}
                isResizable={RESIZE_CONFIG.enabled}
                draggableHandle={DRAG_CONFIG.handle}
                onLayoutChange={handleLayoutChange}
                width={width}
            >
                {children}
            </Responsive>
        </div>
    );
}

export default GridLayoutWrapper;
